/**
 * The words on the home page, generated from the numbers.
 *
 * Deterministic templates, no model in the loop: the same data always reads
 * the same way, and every sentence is covered by __tests__/story.test.ts.
 * Number and change formatting comes from ~/lib/format.
 */

import {
  capitalize,
  countWord,
  fmtHours,
  fmtNum,
  pctChange,
  periodName,
  plural,
  priorName,
} from "~/lib/format";

export interface Pair {
  current: number;
  previous: number;
}

// ---------------------------------------------------------------------------
// Front page lead

export interface HomeStoryInput {
  periodLabel: string;
  prevLabel: string;
  prsMerged: Pair;
  /** Average hours from open to merge. */
  timeToMerge: Pair | null;
  /** Average hours to first review. */
  firstReview: Pair | null;
  deployments: Pair | null;
  issuesResolved: Pair | null;
  storyPoints: Pair | null;
  contributors: Pair;
  momentum: { score: number; label: string } | null;
}

/** A duration counts as having moved when it changed by a quarter or more. */
const DURATION_MOVE = 25;

export function buildHomeHeadline(s: HomeStoryInput): string {
  const when = `in ${periodName(s.periodLabel)}`;
  const cur = s.prsMerged.current;
  const prev = s.prsMerged.previous;
  let lead: string;
  const pct = pctChange(cur, prev);
  if (cur === 0 && prev === 0) {
    lead = `No PRs merged ${when} yet`;
  } else if (prev === 0) {
    lead = `${fmtNum(cur)} PRs merged ${when}, from none`;
  } else if (pct !== null && Math.abs(pct) < 5) {
    lead = `Merged PRs hold steady ${when} at ${fmtNum(cur)}`;
  } else {
    lead = `Merged PRs ${cur > prev ? "up" : "down"} ${Math.abs(pct ?? 0)}% ${when}`;
  }

  const ttm = s.timeToMerge ? pctChange(s.timeToMerge.current, s.timeToMerge.previous) : null;
  if (cur > 0 && ttm !== null && Math.abs(ttm) >= DURATION_MOVE) {
    return ttm > 0
      ? `${lead}, and PRs taking ${ttm}% longer to merge`
      : `${lead}, and PRs merging ${Math.abs(ttm)}% faster`;
  }
  const fr = s.firstReview ? pctChange(s.firstReview.current, s.firstReview.previous) : null;
  if (cur > 0 && fr !== null && Math.abs(fr) >= DURATION_MOVE) {
    return fr > 0
      ? `${lead}, with first reviews ${fr}% slower to arrive`
      : `${lead}, with first reviews arriving ${Math.abs(fr)}% sooner`;
  }
  const people = s.contributors.current - s.contributors.previous;
  if (Math.abs(people) >= 3) {
    return `${lead}, with ${countWord(Math.abs(people))} ${people > 0 ? "more" : "fewer"} people contributing`;
  }
  return lead;
}

export function buildHomeDek(s: HomeStoryInput): string {
  const now = periodName(s.periodLabel);
  const prior = priorName(s.prevLabel);
  const parts: string[] = [];
  parts.push(
    `${capitalize(plural(s.contributors.current, "person", "people"))} merged ${plural(s.prsMerged.current, "pull request")} in ${now}, against ${fmtNum(s.prsMerged.previous)} over ${prior}.`,
  );
  if (s.timeToMerge && s.timeToMerge.current > 0) {
    const was =
      s.timeToMerge.previous > 0
        ? `, ${s.timeToMerge.current <= s.timeToMerge.previous ? "down" : "up"} from ${fmtHours(s.timeToMerge.previous)}`
        : "";
    parts.push(`The average PR took ${fmtHours(s.timeToMerge.current)} from open to merge${was}.`);
  }
  if (s.issuesResolved && s.issuesResolved.current > 0) {
    const pts =
      s.storyPoints && s.storyPoints.current > 0
        ? `, worth ${plural(s.storyPoints.current, "story point")}`
        : "";
    parts.push(`The team resolved ${plural(s.issuesResolved.current, "JIRA issue")}${pts}.`);
  }
  if (s.momentum) {
    parts.push(
      `The momentum score reads ${fmtNum(s.momentum.score)} of 100, where 50 is level with ${prior}.`,
    );
  }
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// Section heads

export interface AttentionCounts {
  stale: number;
  awaitingReview: number;
  large: number;
}

/** The action-items endpoint returns at most this many per list. */
export const ACTION_LIST_CAP = 10;

function atLeast(n: number, cap: number): string {
  return n >= cap ? `At least ${fmtNum(n)}` : capitalize(countWord(n));
}

export function buildAttentionHeadline(c: AttentionCounts): string {
  if (c.stale === 0 && c.awaitingReview === 0 && c.large === 0) {
    return "No open PRs are stuck, waiting or oversized";
  }
  if (c.stale > 0) {
    const lead = `${atLeast(c.stale, ACTION_LIST_CAP)} open ${c.stale === 1 ? "PR has" : "PRs have"} gone a week without activity`;
    if (c.awaitingReview > 0) {
      return `${lead}, and ${countWord(c.awaitingReview)}${c.awaitingReview >= ACTION_LIST_CAP ? " or more" : ""} ${c.awaitingReview === 1 ? "is" : "are"} still waiting on a first review`;
    }
    return lead;
  }
  if (c.awaitingReview > 0) {
    return `${atLeast(c.awaitingReview, ACTION_LIST_CAP)} open ${c.awaitingReview === 1 ? "PR is" : "PRs are"} still waiting on a first review`;
  }
  return `${capitalize(countWord(c.large))} open ${c.large === 1 ? "PR runs" : "PRs run"} past 500 added lines`;
}

export interface CategoryShare {
  name: string;
  value: number;
}

export function buildWorkHeadline(categories: CategoryShare[]): string | null {
  const total = categories.reduce((s, c) => s + c.value, 0);
  if (total === 0) return null;
  const sorted = [...categories].sort((a, b) => b.value - a.value);
  const top = sorted[0]!;
  const share = Math.round((top.value / total) * 100);
  const second = sorted[1];
  const tail =
    second && second.value > 0
      ? `, ahead of ${second.name.toLowerCase()} at ${Math.round((second.value / total) * 100)}%`
      : "";
  return `${top.name} made up ${share}% of merged PRs${tail}`;
}
